/**
 * TokenRefresher
 *
 * A singleton that holds a registered silent-refresh callback so that
 * non-hook code (e.g. fetchService) can request a token refresh without
 * direct access to the Auth0 SDK.
 *
 * Usage:
 *   // Register once, inside TokenHandler on mount:
 *   tokenRefresher.register(getAccessTokenSilently);
 *
 *   // Call from anywhere:
 *   const newToken = await tokenRefresher.tryRefresh();
 *   if (newToken) { ... } else { // refresh failed }
 */

import { createFileLogger } from '../utils/logger';
import { sessionManager } from './sessionManager';
import { API_BASE_URL } from '../api/config';

const log = createFileLogger('TOKEN_REFRESHER');

type RefreshFn = (options?: any) => Promise<string>;

class TokenRefresher {
  private refreshFn: RefreshFn | null = null;
  private audience: string = '';
  // In-flight promise deduplication: concurrent callers share one refresh attempt
  private inFlightRefresh: Promise<string | null> | null = null;

  register(fn: RefreshFn, audience: string): void {
    this.refreshFn = fn;
    this.audience = audience;
    log.log('Refresh function registered');
  }

  /**
   * Attempt a silent token refresh.
   * Concurrent calls are coalesced into a single Auth0 request to avoid
   * refresh token rotation races when multiple 401s arrive simultaneously.
   * Returns the new access token on success, or null on failure.
   * Also stores the new token in localStorage['authToken'] on success.
   */
  async tryRefresh(): Promise<string | null> {
    if (!this.refreshFn) {
      log.warn('No refresh function registered');
      return null;
    }

    // Return the existing in-flight promise if one is already running
    if (this.inFlightRefresh) {
      log.log('Refresh already in progress — coalescing request');
      return this.inFlightRefresh;
    }

    this.inFlightRefresh = this._doRefresh().finally(() => {
      this.inFlightRefresh = null;
    });

    return this.inFlightRefresh;
  }

  private async _doRefresh(): Promise<string | null> {
    // Attempt 1: Auth0 silent refresh (Google OAuth users who have an active Auth0 session).
    if (this.refreshFn) {
      try {
        log.log('Attempting Auth0 silent token refresh...');
        const newToken = await this.refreshFn({
          authorizationParams: {
            audience: this.audience,
            scope: 'openid profile email offline_access',
          },
        });

        if (newToken) {
          localStorage.setItem('authToken', newToken);
          window.dispatchEvent(new Event('auth-refresh'));
          sessionManager.reset();
          log.log('Auth0 silent token refresh succeeded');
          return newToken;
        }
      } catch {
        log.log('Auth0 silent refresh failed — trying backend refresh token...');
      }
    }

    // Attempt 2: Backend refresh token exchange (email/password users).
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) {
      log.warn('No refresh token available — cannot refresh session');
      return null;
    }

    try {
      log.log('Attempting backend refresh token exchange...');
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          localStorage.setItem('authToken', data.access_token);
          if (data.id_token) localStorage.setItem('id_token', data.id_token);
          if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
          window.dispatchEvent(new Event('auth-refresh'));
          sessionManager.reset();
          log.log('Backend refresh token exchange succeeded');
          return data.access_token;
        }
      } else {
        // Refresh token is invalid/expired — clear it so we don't retry with it
        localStorage.removeItem('refresh_token');
        log.warn('Backend refresh token exchange failed — token removed');
      }
    } catch (err) {
      log.warn('Backend refresh token exchange error:', err);
    }

    return null;
  }
}

export const tokenRefresher = new TokenRefresher();
export default tokenRefresher;
